import { useMemo, useState } from 'react'
import Modal from './Modal.jsx'
import { DateRangeField } from './DatePicker.jsx'
import Icon from './Icon.jsx'
import ExistingPeriods from './ExistingPeriods.jsx'
import { weekOf } from '../lib/dates.js'

/**
 * Taking an asset out of service for a run of days — the TimeOffModal, for a vehicle.
 *
 * A range rather than a cell per day for the reason time off is: "at the shop for a fortnight"
 * is one decision, and the thing somebody later edits or cancels is that decision, not the
 * fourteen day-cells it happened to cover. The presets are what actually gets typed: it is in
 * the shop today, or for the week — offered inside the date field the way the time-off dialogs
 * offer theirs, rather than as a second row of controls above it.
 */
export default function AssetDowntimeModal({
  asset,
  date,
  weekStart = 1,
  existing = [],
  editing = null,
  onSave,
  onDelete,
  onClose,
}) {
  const thisWeek = useMemo(() => weekOf(date, weekStart), [date, weekStart])

  const [from, setFrom] = useState(editing?.from || date)
  const [to, setTo] = useState(editing?.to || date)
  const [reason, setReason] = useState(editing?.reason || '')
  const [internalNote, setInternalNote] = useState(editing?.internalNote || '')
  const [busy, setBusy] = useState(false)

  const invalid = !from || !to || to < from

  async function submit(e) {
    e.preventDefault()
    if (invalid || busy) return
    setBusy(true)
    try {
      // Sent even when blank, so emptying the box clears a note rather than leaving it.
      await onSave({ fromDate: from, toDate: to, reason: reason.trim() || null, internalNote: internalNote.trim() })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`${editing ? 'Edit out of service' : 'Out of service'} — ${asset?.name || ''}`}
      onClose={onClose}
      width={460}
    >
      <form onSubmit={submit}>
        <DateRangeField
          label="Period"
          from={from}
          to={to}
          onChange={(f, t) => { setFrom(f); setTo(t) }}
          weekStart={weekStart}
          today={date}
          presets={[
            { label: 'This day', from: date, to: date },
            { label: 'This week', from: thisWeek.from, to: thisWeek.to },
          ]}
        />

        {/* Two boxes because the board has room for a few words and the repair has more than
            that. The reason IS the tile text, and a long one widened every cell of the run on
            the week view; the detail goes below, where only the people filling the board see
            it — a dot on the tile, and the note on hover. */}
        <div className="field">
          <label htmlFor="dt-reason">Reason <span className="hint">optional, shown on the board</span></label>
          <input
            id="dt-reason"
            type="text"
            maxLength={120}
            value={reason}
            placeholder="Brakes, tyre swap, inspection…"
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="dt-note">Repair notes <span className="hint">optional, internal</span></label>
          <textarea
            id="dt-note"
            rows={3}
            maxLength={2000}
            value={internalNote}
            placeholder="What is being done, where, and when it is due back."
            onChange={(e) => setInternalNote(e.target.value)}
          />
        </div>

        {invalid && <div className="notice">The end date is before the start date.</div>}

        {!editing && existing.length > 0 && (
          <div className="field">
            <label>Already out of service</label>
            <ExistingPeriods periods={existing} detail={(d) => d.reason || null} onRemove={onDelete} />
          </div>
        )}

        <div className="modal-actions">
          {editing && (
            <button
              type="button"
              className="btn ghost danger"
              onClick={() => onDelete(editing.id)}
              disabled={busy}
            >
              <Icon name="trash" size={14} />
              Back in service
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={invalid || busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Take out of service'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
